import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CommonModule, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideEnvironmentInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthUserState } from '@coin/shared/auth/data-access';
import { AuthComponentsModule, AuthGmsAngularClientModule, UserState } from '@coin/shared/auth/feature';
import { AuthGmsClientConfig } from '@coin/shared/auth/util';
import { LibGbrModule } from '@coin/shared/gbr/feature';
import { ApiModule as ApiModuleV2, Configuration as ApiConfigurationV2 } from '@coin/shared/core/data-access-v2';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import { NumberFormatCountryIso } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AppEnvironment,
  CustomTranslationLoader,
  provideTranslateDebugStore
} from '@coin/shared/core/data-access-translation-loader';
import { provideDefaultDateFormats } from '@coin/shared/core/util-helpers';
import { AppRoutingModule } from './app-routing.module';
import { httpInterceptorProviders } from './http-interceptors';
import { SharedModule } from './shared/shared.module';

registerLocaleData(localeDe, NumberFormatCountryIso.German, localeDeExtra);

const operatorIcons = [
  'contains',
  'endswith',
  'equal',
  'greater',
  'greaterorequal',
  'less',
  'lessorequal',
  'notequal',
  'dropdocuments',
  'notstartswith',
  'notcontains'
];

function loadOperatorIcons(): void {
  const matIconRegistry = inject(MatIconRegistry);
  const domSanitizer = inject(DomSanitizer);
  operatorIcons.forEach(name =>
    matIconRegistry.addSvgIcon(
      name,
      domSanitizer.bypassSecurityTrustResourceUrl(`../../../assets/svgs/operators/${name}.svg`)
    )
  );
}

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      BrowserAnimationsModule,
      SharedModule,
      LegacyComponentsModule,
      AppRoutingModule,
      AuthComponentsModule,
      AuthGmsAngularClientModule.forRoot({
        ...environment.authentication,
        ...environment.adminAuthentication
      } as AuthGmsClientConfig),
      TranslateModule.forRoot(CustomTranslationLoader.createConfig(AppEnvironment.Admin)),
      ApiModuleV2.forRoot(() => {
        return new ApiConfigurationV2({ basePath: environment.api.baseUrl });
      }),
      LibGbrModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWithDelay:5000'
      })
    ),
    { provide: AuthUserState, useExisting: UserState },
    { provide: LOCALE_ID, useValue: NumberFormatCountryIso.German },
    TranslateModule,
    TranslateService,
    httpInterceptorProviders,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideDefaultDateFormats(),
    provideTranslateDebugStore(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }, // fix for https://jira.siemens-energy.com/browse/GMTEC-22822
    provideEnvironmentInitializer(loadOperatorIcons)
  ]
};
